/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import { Table, message, Modal, Tag } from "antd";
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { insuranceCompanyService } from '../../../services/insuranceService';
import Breadcrumb from '../../../components/ui/Breadcrumb';

const InsuranceCompanyList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);

  const fetchCompanies = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await insuranceCompanyService.getAll({
        page,
        per_page: pageSize,
        search: searchTerm || undefined
      });

      setCompanies((response.data || []).map(c => ({
        id: c.id,
        name: c.name,
        code: c.code,
        phone: c.phone || 'N/A',
        email: c.email || 'N/A',
        is_active: c.is_active,
        created_at: c.created_at
      })));
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error fetching companies:', error);
      message.error('Error al cargar aseguradoras');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchCompanies(pagination.current, pagination.pageSize);
  }, [fetchCompanies, pagination.current, pagination.pageSize]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCompanies(1, pagination.pageSize);
    setPagination({ ...pagination, current: 1 });
  };

  const handleDelete = async () => {
    if (!companyToDelete) return;
    try {
      await insuranceCompanyService.delete(companyToDelete.id);
      message.success('Aseguradora eliminada correctamente');
      setDeleteModalVisible(false);
      setCompanyToDelete(null);
      fetchCompanies(pagination.current, pagination.pageSize);
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Error al eliminar aseguradora';
      message.error(errorMsg);
    }
  };

  const handleTableChange = (newPagination) => {
    setPagination(newPagination);
  };

  const columns = [
    {
      title: "Nombre",
      dataIndex: "name",
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (text, record) => (
        <Link to={`/edit-insurance-company?id=${record.id}`}>{text}</Link>
      )
    },
    {
      title: "Código",
      dataIndex: "code",
      sorter: (a, b) => a.code.localeCompare(b.code)
    },
    {
      title: "Teléfono",
      dataIndex: "phone"
    },
    {
      title: "Email",
      dataIndex: "email"
    },
    {
      title: "Estado",
      dataIndex: "is_active",
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      )
    },
    {
      title: "",
      dataIndex: "actions",
      render: (text, record) => (
        <div className="text-end">
          <div className="dropdown dropdown-action">
            <Link
              to="#"
              className="action-icon dropdown-toggle"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v" />
            </Link>
            <div className="dropdown-menu dropdown-menu-end">
              <Link className="dropdown-item" to={`/insurance-plans?company_id=${record.id}`}>
                <i className="fas fa-list me-2" />
                Ver Planes
              </Link>
              <Link className="dropdown-item" to={`/add-insurance-plan?company_id=${record.id}`}>
                <i className="fas fa-plus me-2" />
                Agregar Plan
              </Link>
              <div className="dropdown-divider" />
              <Link className="dropdown-item" to={`/edit-insurance-company?id=${record.id}`}>
                <i className="far fa-edit me-2" />
                Editar
              </Link>
              <button
                className="dropdown-item"
                onClick={() => {
                  setCompanyToDelete(record);
                  setDeleteModalVisible(true);
                }}
              >
                <i className="far fa-trash-alt me-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      <div className="page-header">
            <div className="row align-items-center">
              <div className="col">
                <h3 className="page-title">Aseguradoras</h3>
                <Breadcrumb items={[{ label: 'Inicio', to: '/' }, { label: 'Aseguradoras' }]} />
              </div>
              <div className="col-auto text-end float-end ms-auto">
                <Link to="/add-insurance-company" className="btn btn-primary">
                  <i className="fas fa-plus me-2" />
                  Agregar Aseguradora
                </Link>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title">Lista de Aseguradoras</h5>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-4">
                      <form onSubmit={handleSearch}>
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Buscar..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                          <button type="submit" className="btn btn-primary">
                            <i className="fas fa-search" />
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>

                  <Table
                    columns={columns}
                    dataSource={companies}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      total: total,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} registros`
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </div>
            </div>
          </div>

      <Modal
        title="Confirmar Eliminación"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setCompanyToDelete(null);
        }}
        okText="Eliminar"
        okButtonProps={{ danger: true }}
      >
        <p>
          ¿Está seguro de eliminar la aseguradora <strong>{companyToDelete?.name}</strong>?
        </p>
      </Modal>
    </>
  );
};

export default InsuranceCompanyList;
